// Pure, date-aware derivations over the state. Nothing here mutates its input.
// People lists are always alphabetical: nothing here ranks team members by performance.
import {
  STAGES, STAGE_KEYS, MONTH_COUNT, MAX_MONTH, MAX_BATCH, MIN_VISIBLE_MONTHS, OPEN_STATUSES, PERFORMANCE_RANGES, BRIEFS, stageByKey, stageIndex,
  stageTarget, monthLabel, batchLabel, plural, STAGE_PIC, BRIEF_PIC, CONTRACT_BATCHES, CONTRACT_MONTHS, WEEKDAYS, POSTING_CALENDAR,
} from './constants.js';
import {
  addDays, datePart, diffDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, monthRange, formatDate, isoDateOfTimestamp, weekdayKey,
} from './dates.js';
import { computePerformance, normalizeRange } from './performance.js';

// One definition everywhere (QA-18): an ACTIVE batch has started and is not done (In Progress, Review or Blocked);
// an OPEN batch is anything not Done (To Do included).
const ACTIVE = new Set(['In Progress', 'Review', 'Blocked']);
const OPEN = new Set(OPEN_STATUSES);
export const isActiveBatch = (b) => ACTIVE.has(b.status);
export const isOpenBatch = (b) => OPEN.has(b.status);
const isActive = isActiveBatch;
const byDue = (a, b) => (a.dueDate || '9999-99-99').localeCompare(b.dueDate || '9999-99-99') || a.batchNumber - b.batchNumber;
const byName = (a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }) || a.id.localeCompare(b.id);

function groupBy(list, key) {
  const map = new Map();
  for (const item of list) {
    const k = item[key];
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(item);
  }
  return map;
}

export function buildIndex(state) {
  const byId = (list) => new Map(list.map((e) => [e.id, e]));
  const monthsByClient = groupBy(state.months, 'clientId');
  for (const list of monthsByClient.values()) list.sort((a, b) => a.monthNumber - b.monthNumber);
  const batchesByMonth = groupBy(state.batches, 'monthId');
  for (const list of batchesByMonth.values()) list.sort((a, b) => a.batchNumber - b.batchNumber);
  return {
    clientsById: byId(state.clients),
    monthsById: byId(state.months),
    batchesById: byId(state.batches),
    teamById: byId(state.team),
    eventsById: byId(state.events),
    monthsByClient,
    batchesByMonth,
    batchesByClient: groupBy(state.batches, 'clientId'),
    eventsByBatch: groupBy(state.events.filter((e) => e.batchId), 'batchId'),
  };
}

// A batch whose month is marked Off is never late (DESIGN §7.5).
export function isOffBatch(index, batch) {
  return !!index?.monthsById?.get(batch.monthId)?.off;
}

// 'Off' for a month marked off; otherwise from its batches and dates.
export function monthStatus(month, monthBatches, today) {
  if (month?.off) return 'Off';
  const batches = monthBatches || [];
  if (batches.length) {
    if (batches.every((b) => b.status === 'Done')) return 'Completed';
    if (batches.some((b) => b.status !== 'To Do')) return 'Active';
    return today >= month.startDate ? 'Active' : 'Upcoming';
  }
  if (today > month.endDate) return 'Completed';
  return today >= month.startDate ? 'Active' : 'Upcoming';
}

export function activeMonthNumber(client, today) {
  if (!client?.startDate || today < client.startDate) return 1;
  const guess = Math.max(1, Math.min(MAX_MONTH, Math.floor(diffDays(client.startDate, today) / 31)));
  for (let n = guess; n <= MAX_MONTH; n++) {
    if (today <= monthRange(client.startDate, n).endDate) return n;
  }
  return MAX_MONTH;
}

// Pass `index` so a batch in an Off month is never overdue.
export function isOverdue(batch, today, index = null) {
  return batch.status !== 'Done' && !!batch.dueDate && batch.dueDate < today && !isOffBatch(index, batch);
}

// Due-date wording for a batch relative to `today` — one vocabulary for every view.
// → { days /* today → due date, null without one */, overdue, tone: 'neutral' | 'warning' | 'danger',
//     text:     '2 days overdue' | 'Due today' | 'Due tomorrow' | 'Due in 3 days' | '',
//     relative: '2 days overdue' | 'Today'     | 'Tomorrow'     | 'in 3 days'     | '' (beside an absolute date) }
// A Done batch is never "due" and a batch without a due date has nothing to say: both get empty wording.
// { off: true } (the batch's month is Off): a past due date reads 'Month off' and is not overdue.
export function dueInfo(batch, today, { off = false } = {}) {
  const none = { days: null, overdue: false, tone: 'neutral', text: '', relative: '' };
  if (!batch?.dueDate) return none;
  const days = diffDays(today, batch.dueDate);
  if (batch.status === 'Done') return { ...none, days };
  if (off && days < 0) return { ...none, days, text: 'Month off', relative: 'Month off' };
  if (days < 0) {
    const text = `${plural(-days, 'day')} overdue`;
    return { days, overdue: true, tone: 'danger', text, relative: text };
  }
  if (days === 0) return { days, overdue: false, tone: 'warning', text: 'Due today', relative: 'Today' };
  if (days === 1) return { days, overdue: false, tone: 'neutral', text: 'Due tomorrow', relative: 'Tomorrow' };
  return { days, overdue: false, tone: 'neutral', text: `Due in ${days} days`, relative: `in ${days} days` };
}

export function teamName(index, id) {
  return (id && index.teamById.get(id)?.name) || 'Unassigned';
}

// ---------- PIC & contract (DESIGN §9) ----------

// `source` is the derive index (buildIndex) or the client record itself.
function clientOf(batch, source) {
  if (source?.clientsById) return source.clientsById.get(batch?.clientId) || null;
  return source && typeof source === 'object' ? source : null;
}

// Who holds one stage of a batch: the client's PIC for it (team id; '' when unset or no longer on the team).
// stageKey = a stage key or a stage-03 brief key ('videographer' | 'editing'). Brief Script + Script → Script PIC;
// Briefs (stage 03 as a whole) + Brief Videographer → Videographer PIC; Brief Video Editing, Video Editing, Result → Editor PIC.
export function stagePic(batch, stageKey, index) {
  const role = STAGE_PIC[stageKey] || BRIEF_PIC[stageKey];
  const id = (role && clientOf(batch, index)?.pic?.[role]) || '';
  return id && (!index?.teamById || index.teamById.has(id)) ? id : '';
}

// Who the batch's current stage waits on: its PIC; at stage 03 the PICs of the briefs not ticked yet (both when both
// are ticked but the stage is still open). A Done batch waits on nobody. → [team id] (no '' entries, no repeats)
export function currentStagePics(batch, index) {
  if (!batch || batch.status === 'Done') return [];
  const stage = batch.currentStage || STAGE_KEYS[0];
  let ids = [stagePic(batch, stage, index)];
  if (stage === 'videoShootEditingBrief') {
    const open = BRIEFS.filter((b) => !batch.briefsDone?.[b.key]);
    ids = (open.length ? open : BRIEFS).map((b) => stagePic(batch, b.key, index));
  }
  return [...new Set(ids.filter(Boolean))];
}

const contractValue = (v, spec) => (Number.isInteger(v) && v >= spec.min && v <= spec.max ? v : spec.default);

// The contract stretched by its off months: walks Month 01, 02… until contractMonths working (not Off) months have
// passed. → { months /* effective length */, off /* off months inside it */, end /* last day of the last month */ }
export function contractSpan(index, client) {
  const want = contractValue(client?.contractMonths, CONTRACT_MONTHS);
  const off = new Set((index.monthsByClient.get(client?.id) || []).filter((m) => m.off).map((m) => m.monthNumber));
  let n = 0;
  let working = 0;
  while (working < want && n < MAX_MONTH) {
    n++;
    if (!off.has(n)) working++;
  }
  return { months: n, off: n - working, end: client?.startDate ? monthRange(client.startDate, n).endDate : '' };
}

// Client header "Batch 3 of 6 · Month 4 of 6 (+1 off)". batchNow = the client's highest batch number (0 = none yet);
// monthNow = the date-based current month (1 before the start date). null for an unknown client.
// → { batchesDone, batchesTotal, batchNow, monthNow, monthsTotal, offMonths, effectiveMonths, effectiveEnd, started, ended, text }
export function contractProgress(state, index, clientId, today) {
  const client = index.clientsById.get(clientId);
  if (!client) return null;
  const batches = index.batchesByClient.get(clientId) || [];
  const span = contractSpan(index, client);
  const batchesTotal = contractValue(client.contractBatches, CONTRACT_BATCHES);
  const monthsTotal = contractValue(client.contractMonths, CONTRACT_MONTHS);
  const batchNow = batches.reduce((m, b) => Math.max(m, b.batchNumber || 0), 0);
  const monthNow = activeMonthNumber(client, today);
  return {
    batchesDone: batches.filter((b) => b.status === 'Done').length,
    batchesTotal, batchNow, monthNow, monthsTotal, offMonths: span.off, effectiveMonths: span.months, effectiveEnd: span.end,
    started: today >= client.startDate, ended: !!span.end && today > span.end,
    text: `Batch ${batchNow} of ${batchesTotal} · Month ${monthNow} of ${monthsTotal}${span.off ? ` (+${span.off} off)` : ''}`,
  };
}

// 'Mon · Wed · Fri' ('' without posting days).
export function postingDaysText(client) {
  const days = new Set(client?.postingDays || []);
  return WEEKDAYS.filter((d) => days.has(d.key)).map((d) => d.label).join(' · ');
}

// Months the client's month selector shows: max(12, the date-based current month, the contract months + its off months
// (DESIGN §9), the highest month holding a batch, an Off flag or notes), capped at 99.
export function monthCountOf(index, client, today) {
  if (!client) return MIN_VISIBLE_MONTHS;
  let n = Math.max(MIN_VISIBLE_MONTHS, activeMonthNumber(client, today), contractSpan(index, client).months);
  for (const m of index.monthsByClient.get(client.id) || []) {
    if (m.monthNumber > n && (m.off || m.notes || (index.batchesByMonth.get(m.id) || []).length)) n = m.monthNumber;
  }
  return Math.min(MAX_MONTH, n);
}

// The client's workspace. months = rows 1..monthCount; a month without a stored record yet comes back as a virtual
// month (id '', off false) — acting on it goes through { clientId, monthNumber } (month.setOff, month.update,
// batch.create), which creates it. Per month: status ('Off' when off), off, batches (count), batchNumbers ([6] → "B6"),
// active, completed, batchList. overdue skips batches in Off months.
export function clientSummary(state, index, clientId, today) {
  const client = index.clientsById.get(clientId);
  const batches = index.batchesByClient.get(clientId) || [];
  const horizon = addDays(today, 14);
  const monthCount = monthCountOf(index, client, today);
  const stored = new Map((index.monthsByClient.get(clientId) || []).map((m) => [m.monthNumber, m]));
  const months = [];
  for (let n = 1; n <= monthCount; n++) {
    const month = stored.get(n)
      || { id: '', clientId, monthNumber: n, ...(client ? monthRange(client.startDate, n) : { startDate: '', endDate: '' }), notes: '', off: false, virtual: true };
    const list = month.id ? index.batchesByMonth.get(month.id) || [] : [];
    months.push({
      month,
      monthNumber: n,
      status: monthStatus(month, list, today),
      off: !!month.off,
      batches: list.length,
      batchNumbers: list.map((b) => b.batchNumber),
      active: list.filter(isActive).length,
      completed: list.filter((b) => b.status === 'Done').length,
      batchList: list,
    });
  }
  return {
    totalMonths: monthCount,
    monthCount,
    offMonths: months.filter((m) => m.off).length,
    activeMonths: months.filter((m) => m.status === 'Active').length,
    totalBatches: batches.length,
    activeBatches: batches.filter(isActive).length,
    completedBatches: batches.filter((b) => b.status === 'Done').length,
    upcomingDeadlines: batches.filter((b) => b.status !== 'Done' && b.dueDate && b.dueDate >= today && b.dueDate <= horizon).length,
    overdue: batches.filter((b) => isOverdue(b, today, index)).length,
    activeMonthNumber: client ? activeMonthNumber(client, today) : 1,
    nextBatchNumber: nextBatchNumber(state, clientId),
    contract: client ? contractProgress(state, index, clientId, today) : null,
    months,
  };
}

// Batch no. helpers for the Add/Edit Batch form (numbers are per client, 1–99).
export function nextBatchNumber(state, clientId) {
  const max = state.batches.reduce((m, b) => (b.clientId === clientId ? Math.max(m, b.batchNumber || 0) : m), 0);
  return Math.min(MAX_BATCH, max + 1);
}
export function batchNumberTaken(state, clientId, batchNumber, exceptBatchId = '') {
  return state.batches.some((b) => b.clientId === clientId && b.id !== exceptBatchId && b.batchNumber === Number(batchNumber));
}

// Batches that count on attention screens (Overview/Dashboard stats, Today, Upcoming): those of Active clients.
// A Paused or Completed client's leftovers never appear there (QA-17); the Workspace still lists them per client.
function liveBatches(state) {
  const live = new Set(state.clients.filter((c) => c.status === 'Active').map((c) => c.id));
  return state.batches.filter((b) => live.has(b.clientId));
}

// Legacy dashboard (pre-v2.1 views). Same definitions as overviewData: batches of Active clients only;
// activeBatches = inProduction = active (In Progress/Review/Blocked) — equal to the sum of the clients' activeBatches.
export function dashboardData(state, index, today) {
  const horizon = addDays(today, 14);
  const weekStart = startOfWeek(today);
  const weekEnd = endOfWeek(today);
  const monthPrefix = today.slice(0, 7);
  const live = liveBatches(state);
  const notDone = live.filter(isOpenBatch);
  const active = live.filter(isActive);

  const stats = {
    activeClients: state.clients.filter((c) => c.status === 'Active').length,
    activeBatches: active.length,
    inProduction: active.length,
    completed: live.filter((b) => b.completedAt && isoDateOfTimestamp(b.completedAt).slice(0, 7) === monthPrefix).length,
    dueThisWeek: notDone.filter((b) => b.dueDate && b.dueDate >= weekStart && b.dueDate <= weekEnd).length,
  };

  const clients = state.clients
    .filter((c) => c.status === 'Active')
    .map((client) => {
      const batches = index.batchesByClient.get(client.id) || [];
      // Earliest open due date, overdue included (LOGIC-D8): the row then reads "2 days overdue", never "No deadlines".
      const next = batches.filter((b) => isOpenBatch(b) && b.dueDate).sort(byDue)[0] || null;
      return {
        client,
        activeMonthNumber: activeMonthNumber(client, today),
        activeBatches: batches.filter(isActive).length,
        nextDue: next ? next.dueDate : '',
        nextDueBatch: next,
        overdue: batches.filter((b) => isOverdue(b, today, index)).length,
      };
    });

  return {
    stats,
    today: {
      overdue: notDone.filter((b) => isOverdue(b, today, index)).sort(byDue),
      dueToday: notDone.filter((b) => b.dueDate === today).sort(byDue),
      inReview: live.filter((b) => b.status === 'Review').sort(byDue),
      // Only stored events; batches due today are already listed in dueToday.
      events: calendarItems(state, index, { from: today, to: today }).filter((i) => i.source === 'event'),
    },
    activeProduction: active.sort(byDue),
    upcoming: notDone.filter((b) => b.dueDate && b.dueDate > today && b.dueDate <= horizon).sort(byDue),
    clients,
  };
}

// Posting calendar (DESIGN §9), derived and never stored: an all-day "Posting · <Client>" item on each posting day of an
// Active client from its start date through the effective contract end (contract months + off months). Days inside an
// Off month are skipped (production and posting pause there; that is why the contract stretches). Not draggable.
function postingItems(state, index, from, to) {
  const out = [];
  for (const client of state.clients) {
    if (client.status !== 'Active' || !client.startDate || !client.postingDays?.length) continue;
    const days = new Set(client.postingDays);
    const offRanges = (index.monthsByClient.get(client.id) || []).filter((m) => m.off);
    const end = contractSpan(index, client).end;
    const last = to < end ? to : end;
    for (let d = from > client.startDate ? from : client.startDate; d <= last; d = addDays(d, 1)) {
      if (!days.has(weekdayKey(d)) || offRanges.some((m) => d >= m.startDate && d <= m.endDate)) continue;
      out.push({
        id: `posting-${client.id}-${d}`, source: 'posting', title: `Posting · ${client.name}`, clientName: client.name,
        start: d, end: '', allDay: true, calendarType: POSTING_CALENDAR.key, kind: 'Posting',
        clientId: client.id, monthId: '', batchId: '', stage: '', owner: '', notes: '', draggable: false, href: hrefs.client(client.id),
      });
    }
  }
  return out;
}

function itemSort(a, b) {
  return datePart(a.start).localeCompare(datePart(b.start))
    || Number(b.allDay) - Number(a.allDay)
    || a.start.localeCompare(b.start)
    || a.title.localeCompare(b.title);
}

export function calendarItems(state, index, { from, to, calendars } = {}) {
  const show = { main: calendars?.main ?? true, internal: calendars?.internal ?? true, posting: calendars?.posting ?? true };
  const items = [];
  for (const e of state.events) {
    if (!show[e.calendarType]) continue;
    const startDate = datePart(e.start);
    const endDate = e.end ? datePart(e.end) : startDate;
    if (startDate > to || endDate < from) continue;
    items.push({
      id: e.id, source: 'event', eventId: e.id, title: e.title, start: e.start, end: e.end, allDay: !!e.allDay,
      calendarType: e.calendarType, kind: e.kind, clientId: e.clientId, monthId: e.monthId, batchId: e.batchId,
      stage: e.stage, owner: e.owner, notes: e.notes,
    });
  }
  if (show.main) {
    for (const b of state.batches) {
      if (!b.dueDate || b.dueDate < from || b.dueDate > to) continue;
      const client = index.clientsById.get(b.clientId);
      items.push({
        // Leads with the batch name so a narrow week column still shows which batch is due (client in clientId).
        id: `due-${b.id}`, source: 'batchDue', title: `${b.name} due`, clientName: client ? client.name : '', batchNumber: b.batchNumber,
        start: b.dueDate, end: '', allDay: true, calendarType: 'main', kind: 'Batch Due',
        clientId: b.clientId, monthId: b.monthId, batchId: b.id, stage: b.currentStage, owner: b.owner, notes: b.notes,
        status: b.status, // extra: lets the calendar mute delivered batches
      });
    }
  }
  if (show.posting && from && to) items.push(...postingItems(state, index, from, to));
  return items.sort(itemSort);
}

// ---------- links & labels ----------

export const hrefs = {
  batch: (id) => `#/batches/${id}`,
  client: (id) => `#/clients/${encodeURIComponent(id)}`,
  month: (clientId, n) => `#/clients/${encodeURIComponent(clientId)}/months/${n}`,
  clients: () => '#/clients',
  person: (id) => `#/people/${encodeURIComponent(id)}`,
  instagram: (handle) => `https://instagram.com/${encodeURIComponent(handle)}`,
  tiktok: (handle) => `https://tiktok.com/@${encodeURIComponent(handle)}`,
  day: (date) => `#/calendar?view=day&date=${datePart(date)}`,
};

function monthNumberOf(index, batch) {
  return index.monthsById.get(batch.monthId)?.monthNumber || 0;
}

// 'Northwind Coffee Co. · Batch 06 · Month 09' — the muted second line of a batch row (DESIGN §7.4).
export function batchContext(index, batch) {
  const client = index.clientsById.get(batch.clientId);
  return [client?.name || 'Unknown client', batchLabel(batch.batchNumber), monthLabel(monthNumberOf(index, batch))].join(' · ');
}

// Deliverables of one stage (default: the current one). → { stage, label, short, done, target, text: '12/15', full, status }
export function stageProgress(batch, stageKey = batch.currentStage) {
  const st = stageByKey(stageKey) || STAGES[0];
  const target = stageTarget(batch, st.key);
  const done = Math.min(target, Math.max(0, Number(batch.progress?.[st.key]) || 0));
  return { stage: st.key, label: st.label, short: st.short, done, target, text: `${done}/${target}`, full: done >= target, status: batch.stageStatus?.[st.key] || 'To Do' };
}

// All five stages' progress (stage 03 also lists its briefs: [{ key, label, urlField, url, done }]).
export function batchStages(batch) {
  return STAGE_KEYS.map((key) => {
    const p = stageProgress(batch, key);
    if (key !== 'videoShootEditingBrief') return p;
    return { ...p, briefs: BRIEFS.map((b) => ({ ...b, url: batch[b.urlField] || '', done: !!batch.briefsDone?.[b.key] })) };
  });
}

// Stage name for one-line texts (rows, feed, search): stage 03 uses its short name "Briefs".
export function stageName(key) {
  const st = stageByKey(key);
  if (!st) return '';
  return st.key === 'videoShootEditingBrief' ? st.short : st.label;
}

// One formatter for every list (same as the StageProgress row label): 'Script · 12/15' / 'Briefs · 1/2' /
// 'Brief Script' (single deliverable, no counter) for started work, else 'Not started' / 'Complete'.
export function stageProgressText(batch) {
  if (batch.status === 'Done') return 'Complete';
  if (!isActive(batch)) return 'Not started';
  const p = stageProgress(batch);
  return p.target === 1 && p.stage === STAGE_KEYS[0] ? p.label : `${stageName(p.stage)} · ${p.text}`;
}

// 'Video Editing' / 'Briefs' for started work, else the status ('To Do', 'Done').
export function stageText(batch) {
  return isActive(batch) ? stageName(batch.currentStage) : batch.status;
}

const firstName = (name) => String(name || '').trim().split(/\s+/)[0] || '';

// ---------- search ----------

const PAGES = [
  { id: 'overview', title: 'Overview', href: '#/', words: 'overview dashboard home today attention' },
  { id: 'workspace', title: 'Workspace', href: '#/workspace', words: 'workspace batches work my work review' },
  { id: 'clients', title: 'Clients', href: '#/clients', words: 'clients accounts months off' },
  { id: 'calendar', title: 'Calendar', href: '#/calendar', words: 'calendar main calendar internal notes schedule events' },
  { id: 'performance', title: 'Performance', href: '#/performance', words: 'performance output on time score trend' },
  { id: 'people', title: 'People', href: '#/people', words: 'people team members' },
  { id: 'settings', title: 'Settings', href: '#/settings', words: 'settings sample data sign out about' },
];

function batchResult(index, batch) {
  return {
    type: 'batch', id: batch.id, title: batch.name,
    subtitle: `${batchContext(index, batch)} · ${stageProgressText(batch)}`,
    context: batchContext(index, batch), stage: stageProgressText(batch),
    status: batch.status, href: hrefs.batch(batch.id),
  };
}

function monthResult(index, client, month, today) {
  const list = index.batchesByMonth.get(month.id) || [];
  return {
    type: 'month', id: month.id, title: `${client.name} · ${monthLabel(month.monthNumber)}`,
    subtitle: [`${monthStatus(month, list, today)} · ${plural(list.length, 'batch', 'batches')}`, month.notes].filter(Boolean).join(' · '),
    href: hrefs.month(client.id, month.monthNumber),
  };
}

const sortBatches = (index) => (a, b) => monthNumberOf(index, a) - monthNumberOf(index, b) || a.batchNumber - b.batchNumber;

// Extracts "month 3" / "batch 4" (also "month03") number filters; the remaining words become plain tokens.
function parseQuery(query) {
  let rest = String(query || '').toLowerCase();
  let month = 0;
  let batch = 0;
  rest = rest.replace(/\bmonth\s*0*(\d{1,2})\b/, (_, n) => { month = Number(n); return ' '; });
  rest = rest.replace(/\bbatch\s*0*(\d{1,3})\b/, (_, n) => { batch = Number(n); return ' '; });
  return { tokens: rest.split(/\s+/).filter(Boolean), month, batch };
}

// Command-palette search. → { batches, clients /* clients and months */, people, pages, events, total }, each a list of
// { type: 'batch'|'client'|'month'|'person'|'page'|'event', id, title, subtitle, href } capped at `limit` per group.
// Searches clients (name, contact, email, phone, website, notes), months (notes; "month 3"), batches (name, notes,
// "batch 4", current stage label), team members (name, role), pages and events (title, notes, kind).
export function search(state, index, query, today, limit = 8) {
  const groups = { batches: [], clients: [], people: [], pages: [], events: [] };
  const { tokens, month, batch } = parseQuery(query);
  if (!tokens.length && !month && !batch) return { ...groups, total: 0 };
  const hay = (...fields) => fields.filter(Boolean).join(' \u0001 ').toLowerCase();
  const matches = (...fields) => {
    const h = hay(...fields);
    return tokens.every((t) => h.includes(t));
  };
  const numberFilter = month > 0 || batch > 0;
  const seen = new Set();
  const push = (group, r) => {
    const key = `${r.type}:${r.id}`;
    if (seen.has(key) || groups[group].length >= limit) return;
    seen.add(key);
    groups[group].push(r);
  };

  // Clients. Only a name/contact match expands into the active month and its active batches (LOGIC-B2).
  const clients = tokens.length ? state.clients.filter((c) => matches(c.name, c.contactName, c.email, c.phone, c.website, c.notes, c.instagram, c.tiktok)) : state.clients;
  if (!numberFilter) {
    for (const c of [...clients].sort(byName)) {
      const n = activeMonthNumber(c, today);
      push('clients', { type: 'client', id: c.id, title: c.name, subtitle: `${c.status} · ${monthLabel(n)} · ${teamName(index, c.owner)}`, href: hrefs.client(c.id) });
      if (!matches(c.name, c.contactName)) continue;
      const m = (index.monthsByClient.get(c.id) || []).find((x) => x.monthNumber === n);
      if (m) push('clients', monthResult(index, c, m, today));
      (index.batchesByClient.get(c.id) || []).filter(isActive).sort(sortBatches(index)).forEach((b) => push('batches', batchResult(index, b)));
    }
  }

  // Months: "month 3" → that month of each matching client; words found in month notes (SPEC-07, LOGIC-D1).
  if (month && !batch) {
    for (const c of clients) {
      const m = (index.monthsByClient.get(c.id) || []).find((x) => x.monthNumber === month);
      if (m) push('clients', monthResult(index, c, m, today));
    }
  }
  if (tokens.length && !batch) {
    for (const m of state.months) {
      if (!m.notes || (month && m.monthNumber !== month)) continue;
      const c = index.clientsById.get(m.clientId);
      const notes = m.notes.toLowerCase();
      if (c && tokens.some((t) => notes.includes(t)) && matches(c.name, monthLabel(m.monthNumber), m.notes)) push('clients', monthResult(index, c, m, today));
    }
  }

  // People (alphabetical) → their page.
  if (!numberFilter && tokens.length) {
    for (const t of state.team.filter((x) => matches(x.name, x.role)).sort(byName)) {
      const active = state.batches.filter((b) => b.owner === t.id && isActive(b)).length;
      push('people', { type: 'person', id: t.id, title: t.name, subtitle: `${t.role || 'Team member'} · ${active} active`, href: hrefs.person(t.id) });
    }
    for (const pg of PAGES.filter((x) => matches(x.title, x.words))) push('pages', { type: 'page', id: pg.id, title: pg.title, subtitle: 'Page', href: pg.href });
  }

  // Stage-label queries ("video editing") list the active batches currently in that stage.
  if (!numberFilter && tokens.length) {
    const phrase = tokens.join(' ');
    const exact = (st) => Number(st.label.toLowerCase() === phrase || st.short.toLowerCase() === phrase);
    for (const st of STAGES.filter((x) => matches(x.label, x.short)).sort((a, b) => exact(b) - exact(a))) {
      state.batches.filter((b) => isActive(b) && b.currentStage === st.key).sort(byDue).forEach((b) => push('batches', batchResult(index, b)));
    }
  }

  // Batches by name / notes; with a number filter the client name also counts ("northwind batch 4"). Open work first.
  state.batches
    .filter((b) => {
      if (month && monthNumberOf(index, b) !== month) return false;
      if (batch && b.batchNumber !== batch) return false;
      return matches(b.name, b.notes, numberFilter ? index.clientsById.get(b.clientId)?.name : '');
    })
    .sort((a, b) => Number(a.status === 'Done') - Number(b.status === 'Done') || (b.dueDate || '').localeCompare(a.dueDate || ''))
    .forEach((b) => push('batches', batchResult(index, b)));

  // Events by title / notes / kind (upcoming first).
  if (!numberFilter && tokens.length) {
    state.events
      .filter((e) => matches(e.title, e.notes, e.kind))
      .sort((a, b) => Number(datePart(a.start) < today) - Number(datePart(b.start) < today) || a.start.localeCompare(b.start))
      .forEach((e) => {
        const client = index.clientsById.get(e.clientId);
        push('events', { type: 'event', id: e.id, title: e.title, subtitle: [formatDate(e.start, 'long'), e.kind, client?.name].filter(Boolean).join(' · '), href: hrefs.day(e.start) });
      });
  }

  const total = Object.values(groups).reduce((n, list) => n + list.length, 0);
  return { ...groups, total };
}

// ---------- activity ----------

// Plain-text feed of the activity log, newest first. A stage completion immediately followed by the next stage's
// start (one "Mark Complete") is shown once, as the move. Entries of deleted batches are skipped. Month entries
// ('monthOff'/'monthOn', batchId '' + monthId, href = the client month) appear only in the unfiltered feed.
// → [{ id, at, actorId, actorName /* '' when unknown */, kind: 'created'|'started'|'moved'|'review'|'blocked'|
//      'completed'|'batchDone'|'assigned'|'progress'|'brief'|'monthOff'|'monthOn', text, batchId, clientId, stage, href }]
export function activityFeed(state, index, { limit = 50, batchId = '', since = '' } = {}) {
  const log = state.activity || [];
  const out = [];
  const undone = batchId ? null : undoneMonthToggles(log);
  for (let i = log.length - 1; i >= 0 && out.length < limit; i--) {
    const a = log[i];
    if ((batchId && a.batchId !== batchId) || (since && a.at < since)) continue;
    if (a.type === 'monthOff' || a.type === 'monthOn') {
      if (batchId || undone.has(a.id)) continue;
      const item = monthActivity(a, index);
      if (item) out.push(item);
      continue;
    }
    const batch = index.batchesById.get(a.batchId);
    if (!batch) continue;
    const later = log[i + 1];
    const merged = a.type === 'completed' && later && later.batchId === a.batchId && later.at === a.at
      && later.actorId === a.actorId && (later.type === 'started' || later.type === 'batchDone');
    if (merged) continue; // the following 'started'/'batchDone' entry says it
    const actorName = (a.actorId && index.teamById.get(a.actorId)?.name) || '';
    const { kind, text } = describeActivity(a, batch, firstName(actorName), index, !!batchId);
    out.push({ id: a.id, at: a.at, actorId: a.actorId || '', actorName, kind, text, batchId: a.batchId, clientId: a.clientId, stage: a.stage || '', href: hrefs.batch(a.batchId) });
  }
  return out;
}

// A month marked off and resumed again by the same person within 10 minutes (a mis-click and its undo) says
// nothing: both entries are left out of the feed. → Set of activity ids
const UNDO_WINDOW_MS = 10 * 60 * 1000;
function undoneMonthToggles(log) {
  const lastOff = new Map();
  const out = new Set();
  for (const a of log) {
    if (a.type === 'monthOff') lastOff.set(a.monthId, a);
    else if (a.type === 'monthOn') {
      const off = lastOff.get(a.monthId);
      if (off && off.actorId === a.actorId && Date.parse(a.at) - Date.parse(off.at) <= UNDO_WINDOW_MS) { out.add(off.id); out.add(a.id); }
      lastOff.delete(a.monthId);
    }
  }
  return out;
}

// 'Nadia marked Month 03 off · Atlas Fitness Club' / 'Nadia resumed Month 03 · Atlas Fitness Club'.
function monthActivity(a, index) {
  const month = index.monthsById.get(a.monthId);
  const client = index.clientsById.get(a.clientId);
  if (!month || !client) return null;
  const actorName = (a.actorId && index.teamById.get(a.actorId)?.name) || '';
  const who = firstName(actorName);
  const label = monthLabel(month.monthNumber);
  const text = a.type === 'monthOff'
    ? (who ? `${who} marked ${label} off · ${client.name}` : `${label} marked off · ${client.name}`)
    : (who ? `${who} resumed ${label} · ${client.name}` : `${label} resumed · ${client.name}`);
  return {
    id: a.id, at: a.at, actorId: a.actorId || '', actorName, kind: a.type, text, batchId: '', clientId: a.clientId, monthId: a.monthId,
    stage: '', href: hrefs.month(a.clientId, month.monthNumber),
  };
}

// inBatch (the feed of one batch page): the batch's own name is left out ("Sarah moved it to Script").
function describeActivity(a, batch, who, index, inBatch = false) {
  const name = inBatch ? 'the batch' : batch.name;
  const it = inBatch ? 'it' : batch.name;
  const tail = inBatch ? '' : ` · ${batch.name}`;
  const Name = inBatch ? 'The batch' : batch.name;
  const label = stageName(a.stage);
  switch (a.type) {
    case 'created':
      return { kind: 'created', text: who ? `${who} added ${name}` : `${Name} added` };
    case 'started':
      if (stageIndex(a.stage) <= 0) return { kind: 'started', text: who ? `${who} started ${name}` : `${Name} started` };
      return { kind: 'moved', text: who ? `${who} moved ${it} to ${label}` : `${Name} moved to ${label}` };
    case 'review':
      return { kind: 'review', text: `Review requested · ${label}${tail}` };
    case 'blocked':
      return { kind: 'blocked', text: inBatch ? `Blocked at ${label}` : `${name} is blocked at ${label}` };
    case 'completed':
      return { kind: 'completed', text: who ? `${who} completed ${label}${tail}` : `${label} completed${tail}` };
    case 'batchDone':
      return { kind: 'batchDone', text: who ? `${who} finished ${name}` : `${Name} is done` };
    case 'progress':
      return { kind: 'progress', text: who ? `${who} updated ${label} to ${a.detail}${tail}` : `${label} at ${a.detail}${tail}` };
    case 'brief': {
      const [which, state] = String(a.detail || '').split(':');
      const brief = BRIEFS.find((b) => b.key === which)?.label || 'Brief';
      const verb = state === 'done' ? 'completed' : 'reopened';
      return { kind: 'brief', text: who ? `${who} ${verb} ${brief}${tail}` : `${brief} ${verb}${tail}` };
    }
    case 'assigned': {
      const to = a.detail ? teamName(index, a.detail) : '';
      if (!to) return { kind: 'assigned', text: who ? `${who} unassigned ${it}` : `${Name} unassigned` };
      if (who && a.actorId === a.detail) return { kind: 'assigned', text: `${who} took ${it} over` }; // not "Alex assigned it to Alex"
      return { kind: 'assigned', text: who ? `${who} assigned ${it} to ${firstName(to)}` : `${Name} assigned to ${firstName(to)}` };
    }
    default:
      return { kind: a.type, text: Name };
  }
}

// ---------- overview ----------

function deadlineRow(index, batch, today) {
  return {
    id: batch.id, batchId: batch.id, title: batch.name, subtitle: batchContext(index, batch), dueDate: batch.dueDate,
    due: dueInfo(batch, today, { off: isOffBatch(index, batch) }), status: batch.status, owner: batch.owner || '', ownerName: teamName(index, batch.owner), href: hrefs.batch(batch.id),
  };
}

// Right panel of the Overview. → { activity: activityFeed items (newest 7, one per batch + person),
//   deadlines: [{ id, batchId, title, subtitle, dueDate, due: dueInfo, status, owner, ownerName, href }] (overdue + due today, oldest first),
//   upcoming: [{ id, date, time /* 'HH:MM' | '' */, title, kind: 'due'|'event', href }] (tomorrow … +7 days, max 6) }
export function todayPanel(state, index, today) {
  const live = liveBatches(state).filter(isOpenBatch);
  const deadlines = live.filter((b) => b.dueDate && (b.dueDate === today || isOverdue(b, today, index))).sort(byDue).map((b) => deadlineRow(index, b, today));
  const from = addDays(today, 1);
  const to = addDays(today, 7);
  const upcoming = [
    ...live.filter((b) => b.dueDate >= from && b.dueDate <= to)
      .map((b) => ({ id: `due-${b.id}`, date: b.dueDate, time: '', title: `${b.name} due`, kind: 'due', href: hrefs.batch(b.id) })),
    ...state.events.filter((e) => e.calendarType === 'main' && datePart(e.start) >= from && datePart(e.start) <= to)
      .map((e) => ({ id: e.id, date: datePart(e.start), time: e.allDay ? '' : e.start.slice(11, 16), title: e.title, kind: 'event', href: hrefs.day(e.start) })),
  ].sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time) || a.title.localeCompare(b.title)).slice(0, 6);
  // One line per batch and person: a run of steps on one batch (moved… moved… finished) shows as its newest line.
  const seen = new Set();
  const activity = activityFeed(state, index, { limit: 100 }).filter((a) => {
    const key = a.batchId ? `${a.batchId}:${a.actorId}` : a.id;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).slice(0, 7);
  return { activity, deadlines, upcoming };
}

const PERIOD_LABELS = { today: 'Due today', week: 'Due this week', month: 'Due this month' };

// Overview page data. period: 'today' | 'week' | 'month' (OVERVIEW_PERIODS); range: chart range '7d' | '30d' | '3m'.
// Current-work numbers count batches of Active clients only; completions count every client (as on Performance).
// → { period: { key, start, end },
//     cards: { activeBatches: { value, addedThisWeek }, due: { value, overdue, label }, waitingReview: { value },
//              completed: { value, previous, delta /* % or null */ } },
//     production: { range, points: [{ date, label, value }], completed, onTimeRate },
//     side: { onTime: { value, delta /* pp */ }, output: { value, delta /* % */ }, videos: { value, delta /* % */ }, activeEditing, waitingReview } }
export function overviewData(state, index, today, period = 'today', { range = '30d' } = {}) {
  const key = Object.hasOwn(PERIOD_LABELS, period) ? period : 'today';
  const start = key === 'week' ? startOfWeek(today) : key === 'month' ? startOfMonth(today) : today;
  const end = key === 'week' ? endOfWeek(today) : key === 'month' ? endOfMonth(today) : today;
  const live = liveBatches(state);
  const open = live.filter(isOpenBatch);
  const active = live.filter(isActive);
  const weekStart = startOfWeek(today);

  const doneOn = (b) => (b.status === 'Done' && b.completedAt ? isoDateOfTimestamp(b.completedAt) : '');
  const doneBetween = (from, to) => state.batches.filter((b) => { const d = doneOn(b); return d && d >= from && d <= to; }).length;
  const len = diffDays(start, today) + 1;
  const completed = doneBetween(start, today);
  const previous = doneBetween(addDays(start, -len), addDays(start, -1));

  const perf = computePerformance(state, index, { range, today });
  const points = [];
  for (let d = perf.period.start; d <= today; d = addDays(d, 1)) points.push({ date: d, label: formatDate(d), value: 0 });
  const slot = new Map(points.map((p) => [p.date, p]));
  for (const b of state.batches) { const p = slot.get(doneOn(b)); if (p) p.value += 1; }

  const waitingReview = live.filter((b) => b.status === 'Review').length;
  return {
    period: { key, start, end },
    cards: {
      activeBatches: { value: active.length, addedThisWeek: active.filter((b) => b.startedAt && isoDateOfTimestamp(b.startedAt) >= weekStart).length },
      due: { value: open.filter((b) => b.dueDate && b.dueDate >= today && b.dueDate <= end).length, overdue: open.filter((b) => isOverdue(b, today, index)).length, label: PERIOD_LABELS[key] },
      waitingReview: { value: waitingReview },
      completed: { value: completed, previous, delta: previous ? Math.round(((completed - previous) / previous) * 1000) / 10 : null },
    },
    production: { range: perf.range, points, completed: perf.kpis.output.value, onTimeRate: perf.kpis.onTimeRate.value },
    side: {
      onTime: { value: perf.kpis.onTimeRate.value, delta: perf.kpis.onTimeRate.delta },
      output: { value: perf.kpis.output.value, delta: perf.kpis.output.delta },
      videos: { value: perf.kpis.videos.value, delta: perf.kpis.videos.delta },
      activeEditing: live.filter((b) => b.status === 'In Progress' && b.currentStage === 'videoEditing').length,
      waitingReview,
    },
  };
}

// ---------- workspace ----------

export const WORKSPACE_FILTERS = ['all', 'mine', 'review', 'done'];

// One BatchRow's data.
export function batchRow(index, batch, today) {
  const owner = batch.owner ? index.teamById.get(batch.owner) : null;
  return {
    id: batch.id, batch, name: batch.name, batchNumber: batch.batchNumber, clientId: batch.clientId,
    clientName: index.clientsById.get(batch.clientId)?.name || 'Unknown client', monthNumber: monthNumberOf(index, batch),
    subtitle: batchContext(index, batch), stage: batch.currentStage, stageIndex: stageIndex(batch.currentStage), stageLabel: stageText(batch),
    progress: stageProgress(batch), stageText: stageProgressText(batch), videoCount: batch.videoCount, monthOff: isOffBatch(index, batch),
    owner: batch.owner || '', ownerName: owner?.name || '', ownerFirst: firstName(owner?.name), dueDate: batch.dueDate || '',
    due: dueInfo(batch, today, { off: isOffBatch(index, batch) }), status: batch.status, href: hrefs.batch(batch.id),
  };
}

// Workspace table. filter: 'all' (open batches by due date) | 'mine' (open, owned by personId or with its current stage's
// PIC = personId) | 'review' (status Review)
// | 'done' (most recently completed first). Without clientId only Active clients' batches are listed; with clientId the
// client's batches (optionally one monthNumber). query matches batch name, client name, owner name and notes.
// → { rows: [batchRow], total /* before limit */, counts: { all, mine, review, done } /* same scope + query */ }
export function workspaceList(state, index, { filter = 'all', personId = '', clientId = '', monthNumber = 0, query = '', today, limit = 0 } = {}) {
  const f = WORKSPACE_FILTERS.includes(filter) ? filter : 'all';
  const month = Number(monthNumber) || 0;
  const tokens = String(query || '').toLowerCase().split(/\s+/).filter(Boolean);
  const scope = (clientId ? index.batchesByClient.get(clientId) || [] : liveBatches(state)).filter((b) => {
    if (month && monthNumberOf(index, b) !== month) return false;
    if (!tokens.length) return true;
    const hay = [b.name, b.notes, index.clientsById.get(b.clientId)?.name, teamName(index, b.owner)].join(' \u0001 ').toLowerCase();
    return tokens.every((t) => hay.includes(t));
  });
  const tests = {
    all: isOpenBatch,
    // DESIGN §9: mine = I own it, or its current stage waits on me as the client's PIC.
    mine: (b) => isOpenBatch(b) && !!personId && (b.owner === personId || currentStagePics(b, index).includes(personId)),
    review: (b) => b.status === 'Review',
    done: (b) => b.status === 'Done',
  };
  const counts = Object.fromEntries(WORKSPACE_FILTERS.map((k) => [k, scope.filter(tests[k]).length]));
  const list = scope.filter(tests[f]);
  if (f === 'done') list.sort((a, b) => (b.completedAt || '').localeCompare(a.completedAt || '') || byDue(a, b));
  else list.sort((a, b) => byDue(a, b) || monthNumberOf(index, a) - monthNumberOf(index, b));
  const rows = (limit > 0 ? list.slice(0, limit) : list).map((b) => batchRow(index, b, today));
  return { rows, total: list.length, counts };
}

// ---------- notifications ----------

// 'Script is ready for you · Batch 03 · <Client>' when personId is the PIC of the stage that started, else ''. At stage
// 03 the brief they hold is named (both briefs → 'Briefs').
function readyText(batch, a, personId, index) {
  const stageKey = a.stage;
  // Newer entries name the PIC(s) of that moment; older ones only count while that stage is still the current one.
  const stored = typeof a.detail === 'string' && a.detail.startsWith('pic:') ? a.detail.slice(4).split(',') : null;
  if (!stored && (batch.status === 'Done' || batch.currentStage !== stageKey)) return '';
  const picOf = (key, i) => (stored ? stored[i] || '' : stagePic(batch, key, index));
  let label = '';
  if (stageKey === 'videoShootEditingBrief') {
    const held = BRIEFS.filter((b, i) => picOf(b.key, i) === personId);
    label = held.length === 1 ? held[0].label : held.length ? stageName(stageKey) : '';
  } else if (picOf(stageKey, 0) === personId) label = stageName(stageKey);
  if (!label) return '';
  const client = index.clientsById.get(batch.clientId);
  return `${label} is ready for you · ${batchLabel(batch.batchNumber)}${client ? ` · ${client.name}` : ''}`;
}

// For the current person, newest first (max 20): work assigned to them, review requested / stage completed / blocked on
// batches they own (by someone else), and their deadlines (due today, overdue). The UI marks items newer than its
// "seen" timestamp as unread. → [{ id, at, kind: 'assigned'|'review'|'completed'|'blocked'|'due'|'overdue', text, batchId, href }]
export function notificationsFor(state, index, personId, today) {
  if (!personId || !index.teamById.has(personId)) return [];
  const out = [];
  const feed = activityFeed(state, index, { limit: 400 });
  const raw = new Map((state.activity || []).map((a) => [a.id, a]));
  // When this person last became a batch's owner: earlier news of that batch was someone else's (ROLES-06).
  const ownerSince = new Map();
  for (const a of state.activity || []) {
    if ((a.type === 'assigned' || a.type === 'created') && a.detail === personId) ownerSince.set(a.batchId, a.at);
  }
  for (const item of feed) {
    const a = raw.get(item.id);
    const batch = index.batchesById.get(item.batchId);
    if (!a || !batch || a.actorId === personId) continue;
    const mine = batch.owner === personId && a.at >= (ownerSince.get(batch.id) || '');
    // The account owner hears when a batch of their client waits for review or is blocked (ROLES-05).
    const account = index.clientsById.get(batch.clientId)?.owner === personId;
    let kind = '';
    let text = item.text;
    if ((a.type === 'assigned' || a.type === 'created') && a.detail === personId) {
      kind = 'assigned'; // new work for them ("created" entries carry the first owner as detail)
      text = item.actorName ? `${firstName(item.actorName)} assigned ${batch.name} to you` : `${batch.name} was assigned to you`;
    }
    // DESIGN §9: a stage whose PIC is this person started → "Script is ready for you · Batch 03 · Northwind Coffee Co."
    else if (a.type === 'started' && readyText(batch, a, personId, index)) {
      kind = 'assigned';
      text = readyText(batch, a, personId, index);
    }
    else if ((mine || account) && a.type === 'review') kind = 'review';
    else if ((mine || account) && a.type === 'blocked') kind = 'blocked';
    else if (mine && (item.kind === 'moved' || item.kind === 'completed' || item.kind === 'batchDone')) kind = 'completed';
    if (kind) out.push({ id: item.id, at: item.at, kind, text, batchId: item.batchId, href: item.href });
  }
  // Deadlines of the batches this person owns or whose current stage waits on them (ROLES-05), within the same scope as
  // Overview and Workspace: Active clients only (LOGIC-08).
  const dayStart = new Date(`${today}T00:00:00`).toISOString();
  const deadlines = [];
  for (const b of state.batches) {
    if (!isOpenBatch(b) || !b.dueDate || b.dueDate > today) continue;
    if (b.owner !== personId && !currentStagePics(b, index).includes(personId)) continue;
    if (index.clientsById.get(b.clientId)?.status !== 'Active') continue;
    if (b.dueDate < today && isOffBatch(index, b)) continue;
    const info = dueInfo(b, today);
    deadlines.push({ id: `due-${b.id}-${today}`, at: dayStart, kind: info.overdue ? 'overdue' : 'due', text: `${b.name} · ${info.text}`, batchId: b.id, href: hrefs.batch(b.id), dueDate: b.dueDate });
  }
  // Deadlines come first (oldest due date first), so a busy day of activity never pushes them out of the bell (LOGIC-05).
  deadlines.sort((x, y) => x.dueDate.localeCompare(y.dueDate));
  out.sort((x, y) => y.at.localeCompare(x.at));
  return [...deadlines.map(({ dueDate, ...d }) => d), ...out].slice(0, 20);
}

// ---------- people ----------

const trendOf = (delta) => (delta == null || delta === 0 ? 'flat' : delta > 0 ? 'up' : 'down');

// Team list for Performance/People — ALWAYS alphabetical by name, never ordered by any number.
// → [{ id, person, name, role, completed /* in range */, onTimeRate, score, delta /* score pts */, trend: 'up'|'down'|'flat',
//      active /* now */, href }]
// "Active now" / open batches follow the My Work rule (DESIGN §9): batches they own or whose current stage waits on them.
const worksOn = (b, id, index) => b.owner === id || currentStagePics(b, index).includes(id);
// Optional Client/Month scope (Performance scope dropdowns).
export const activeNow = (state, index, id, { clientId = '', monthNumber = 0 } = {}) => state.batches.filter((b) => isActiveBatch(b)
  && worksOn(b, id, index) && (!clientId || b.clientId === clientId)
  && (!Number(monthNumber) || index.monthsById.get(b.monthId)?.monthNumber === Number(monthNumber))).length;

export function peopleStats(state, index, { range = '30d', today } = {}) {
  return [...state.team].sort(byName).map((person) => {
    const p = computePerformance(state, index, { range, today, teamMemberId: person.id });
    p.kpis.activeWorkload.value = activeNow(state, index, person.id);
    return {
      id: person.id, person, name: person.name, role: person.role || '',
      completed: p.kpis.output.value, videos: p.kpis.videos.value, onTimeRate: p.kpis.onTimeRate.value, score: p.kpis.score.value,
      delta: p.kpis.score.delta, trend: trendOf(p.kpis.score.delta), active: p.kpis.activeWorkload.value, href: hrefs.person(person.id),
    };
  });
}

// One person's page. → null for an unknown id, else { id, person, name, role, range, period, openBatches: [batchRow]
//   (their unfinished batches by due date), kpis /* computePerformance kpis for their batches */, series /* score */,
//   buckets /* [{ key, label, start, end, output, onTimeRate, completionRate, score }] */, trend }
export function personStats(state, index, personId, { range = '30d', today } = {}) {
  const person = index.teamById.get(personId);
  if (!person) return null;
  const p = computePerformance(state, index, { range, today, teamMemberId: person.id });
  p.kpis.activeWorkload.value = activeNow(state, index, person.id);
  const open = state.batches.filter((b) => isOpenBatch(b) && worksOn(b, person.id, index)).sort(byDue);
  return {
    id: person.id, person, name: person.name, role: person.role || '', range: p.range, period: p.period,
    openBatches: open.map((b) => batchRow(index, b, today)),
    kpis: p.kpis, series: p.series,
    buckets: p.buckets.map(({ key, label, start, end, output, videos, onTimeRate, completionRate, score }) => ({ key, label, start, end, output, videos, onTimeRate, completionRate, score })),
    trend: trendOf(p.kpis.score.delta),
  };
}
